import { createTheme, ThemeProvider } from "@material-ui/core/styles";
import Config from "Config";
import jwt_decode from "jwt-decode";
import moment from "moment-timezone";
import React, { Suspense, useEffect } from "react";
import { useCookies } from "react-cookie";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
    unstable_HistoryRouter as HistoryRouter,
    Navigate,
    Route,
    Routes,
} from "react-router-dom";
import { setLocale } from "yup";
import "./App.css";
import "./common.scss";
import AlertDialog from "./component/AlertDialog/AlertDialog";
import Auth from "./component/Auth/Auth";
import { AuthProvider } from "./component/AuthContext/AuthContext";
import CCCUpdatePage from "./component/CCCUpdatePage/CCCUpdatePage";
import DelegationPage from "./component/DelegationPage/DelegationPage";
import EditYear from "./component/EditYearPage/EditYear";
import Forbidden from "./component/Forbidden/Forbidden";
import FormEnquiry from "./component/FormEnquiry/FormEnquiry";
import FormGuard from "./component/FormGuard/FormGuard";
import Header from "./component/Header/Header";
import HealthCheck from "./component/HealthCheckPage/HealthCheck";
import HistoryEnquiry from "./component/HistoryEnquiry/HistoryEnquiry";
import Authentication from "./component/IAMSmartPage/AuthenticationPage/Authentication";
import Loading from "./component/Loading/Loading";
import AfterLoginCLO from "./component/LoginPage/AfterLoginCLO";
import Login from "./component/LoginPage/Login";
import LoginCLO from "./component/LoginPage/LoginCLO";
import LoginIAMSmart from "./component/LoginPage/LoginIAMSmart";
import LoginPublic from "./component/LoginPage/LoginPublic";
import LoginSSLO from "./component/LoginPage/LoginSSLO";
import Main from "./component/LoginPage/Main";
import MonthlySubmissionReport from "./component/MonthlySubmissionReport/MonthlySubmissionReport";
import ParameterPage from "./component/ParameterPage/ParameterPage";
import Enquiry from "./component/PortalNavbarPage/EnquiryPage/Enquiry";
import Faq from "./component/PortalNavbarPage/FaqPage/Faq";
import MaintenanceTime from "./component/PortalNavbarPage/MaintenanceTimePage/MaintenanceTime";
import OnlineTutorial from "./component/PortalNavbarPage/OnlineTutorialPage/OnlineTutorial";
import ProfilePage from "./component/ProfilePage/ProfilePage";
import useTimeout from "./component/TimeoutDialog/Timeout";
import TimeoutDialog from "./component/TimeoutDialog/TimeoutDialog";
import { history } from "./history";
import IamsmartAnonFormFilling from "./page/Iamsmart/IamsmartAnonFormFilling";
import IamsmartAnonSigning from "./page/Iamsmart/IamsmartAnonSigning";
import IamsmartLogin from "./page/Iamsmart/IamsmartLogin";
import NotFound from "./page/NotFound/NotFound";
import ChangePassword from "./page/Portal/ChangePassword";
import EditFormOwner from "./page/Portal/EditFormOwner";
import EditOrgEdb from "./page/Portal/EditOrgEdb";
import EditPostSecondary from "./page/Portal/EditPostSecondary";
import EditPurpose from "./page/Portal/EditPurpose";
import EditReviewerEdb from "./page/Portal/EditReviewerEdb";
import EditStaff from "./page/Portal/EditStaff";
import SystemMessage from "./page/Portal/SystemMessage";

import ForgotPassword from "./page/Portal/ForgotPassword";
import {
    getCloParameter,
    getFormTitleParam,
    getSelectFormList,
    initAnon,
    logout,
    parseToken,
    setCurrentStepWithRerender,
    setFullFieldValues,
    toggleLoadFormPopup,
    updateProfile,
    updateUserRole,
} from "./redux/action";
import {
    getFormTemp,
    getLoading,
    getPopupStatus,
    getSelectedUserOrg,
    getServerTime,
    getToken,
    getUserRole,
} from "./redux/selector";
import DatePickerSampleForm from "./sample/DatePickerSample/DatePickerSampleForm";
import InputSampleForm from "./sample/InputSample/InputSampleForm";
import MultiColumnSampleForm from "./sample/MultiColumnSample/MultiColumnSampleForm";
import TableInputSampleForm from "./sample/TableInputSample/TableInputSampleForm";
import {
    getIsHideSearchPageRoles,
    isNotEmpty,
    isObjectNotEmpty,
} from "./Utils";

/*const redirect = localStorage.getItem("login_redirect");
if (redirect && redirect != "" && redirect != "null") {
    window.location.href = redirect;
    localStorage.removeItem("login_redirect");
}*/

const FormSelector = React.lazy(() =>
    import("./component/FormSelector/FormSelector")
);
const Form001 = React.lazy(() => import("./form/Form001/Form001"));
const Form002 = React.lazy(() => import("./form/Form002/Form002"));
const Form003 = React.lazy(() => import("./form/Form003/Form003"));
const Form004 = React.lazy(() => import("./form/Form004/Form004"));
const Form005 = React.lazy(() => import("./form/Form005/Form005"));
const Form006 = React.lazy(() => import("./form/Form006/Form006"));
const Form007 = React.lazy(() => import("./form/Form007/Form007"));
const Form008 = React.lazy(() => import("./form/Form008/Form008"));
const Form009 = React.lazy(() => import("./form/Form009/Form009"));
const Form010 = React.lazy(() => import("./form/Form010/Form010"));
const Form011 = React.lazy(() => import("./form/Form011/Form011"));
const Form012 = React.lazy(() => import("./form/Form012/Form012"));
const Form013 = React.lazy(() => import("./form/Form013/Form013"));
const Form014 = React.lazy(() => import("./form/Form014/Form014"));
const Form015 = React.lazy(() => import("./form/Form015/Form015"));
const Form016 = React.lazy(() => import("./form/Form016/Form016"));
const Form017 = React.lazy(() => import("./form/Form017/Form017"));
const Form018 = React.lazy(() => import("./form/Form018/Form018"));
const Form019 = React.lazy(() => import("./form/Form019/Form019"));
const Form021 = React.lazy(() => import("./form/Form021/Form021"));
const Form022 = React.lazy(() => import("./form/Form022/Form022"));
const Form023 = React.lazy(() => import("./form/Form023/Form023"));
const Form026 = React.lazy(() => import("./form/Form026/Form026"));
const Form027 = React.lazy(() => import("./form/Form027/Form027"));
const Form028 = React.lazy(() => import("./form/Form028/Form028"));
const Form029 = React.lazy(() => import("./form/Form029/Form029"));
const Form030 = React.lazy(() => import("./form/Form030/Form030"));
const Form031 = React.lazy(() => import("./form/Form031/Form031"));
const Form032 = React.lazy(() => import("./form/Form032/Form032"));
const Form033 = React.lazy(() => import("./form/Form033/Form033"));
const Form034 = React.lazy(() => import("./form/Form034/Form034"));
const Form035 = React.lazy(() => import("./form/Form035/Form035"));
const Form040 = React.lazy(() => import("./form/Form040/Form040"));
const Form041 = React.lazy(() => import("./form/Form041/Form041"));
const Form042 = React.lazy(() => import("./form/Form042/Form042"));
const Form043 = React.lazy(() => import("./form/Form043/Form043"));
const Form044 = React.lazy(() => import("./form/Form044/Form044"));
const Form045 = React.lazy(() => import("./form/Form045/Form045"));
const Form046 = React.lazy(() => import("./form/Form046/Form046"));
const Form047 = React.lazy(() => import("./form/Form047/Form047"));
const Form048 = React.lazy(() => import("./form/Form048/Form048"));
const Form049 = React.lazy(() => import("./form/Form049/Form049"));
const Form050 = React.lazy(() => import("./form/Form050/Form050"));
const Form051 = React.lazy(() => import("./form/Form051/Form051"));
const Form052 = React.lazy(() => import("./form/Form052/Form052"));
const Form053 = React.lazy(() => import("./form/Form053/Form053"));
const Form054 = React.lazy(() => import("./form/Form054/Form054"));
const Form057 = React.lazy(() => import("./form/Form057/Form057"));
const Form058 = React.lazy(() => import("./form/Form058/Form058"));
const Form059 = React.lazy(() => import("./form/Form059/Form059"));
const Form062 = React.lazy(() => import("./form/Form062/Form062"));
const Form063 = React.lazy(() => import("./form/Form063/Form063"));
const Form064 = React.lazy(() => import("./form/Form064/Form064"));
const Form065 = React.lazy(() => import("./form/Form065/Form065"));
const Form066 = React.lazy(() => import("./form/Form066/Form066"));
const Form067 = React.lazy(() => import("./form/Form067/Form067"));
const Form068 = React.lazy(() => import("./form/Form068/Form068"));
const Form069 = React.lazy(() => import("./form/Form069/Form069"));
const Form070 = React.lazy(() => import("./form/Form070/Form070"));
const Form071 = React.lazy(() => import("./form/Form071/Form071"));
const Form072 = React.lazy(() => import("./form/Form072/Form072"));
const Form073 = React.lazy(() => import("./form/Form073/Form073"));
const Form074 = React.lazy(() => import("./form/Form074/Form074"));
const Form075 = React.lazy(() => import("./form/Form075/Form075"));
const Form076 = React.lazy(() => import("./form/Form076/Form076"));
const Form078 = React.lazy(() => import("./form/Form078/Form078"));
const Form079 = React.lazy(() => import("./form/Form079/Form079"));
const Form080 = React.lazy(() => import("./form/Form080/Form080"));
const Form081 = React.lazy(() => import("./form/Form081/Form081"));
const Form082 = React.lazy(() => import("./form/Form082/Form082"));
const Form083 = React.lazy(() => import("./form/Form083/Form083"));
const Form084 = React.lazy(() => import("./form/Form084/Form084"));
const Form085 = React.lazy(() => import("./form/Form085/Form085"));
const Form086 = React.lazy(() => import("./form/Form086/Form086"));
const Form087 = React.lazy(() => import("./form/Form087/Form087"));
const Form088 = React.lazy(() => import("./form/Form088/Form088"));
const Form089 = React.lazy(() => import("./form/Form089/Form089"));
const Form090 = React.lazy(() => import("./form/Form090/Form090"));
const Form091 = React.lazy(() => import("./form/Form091/Form091"));
const Form092 = React.lazy(() => import("./form/Form092/Form092"));
const Form093 = React.lazy(() => import("./form/Form093/Form093"));
const Form094 = React.lazy(() => import("./form/Form094/Form094"));
const Form095 = React.lazy(() => import("./form/Form095/Form095"));
const Form096 = React.lazy(() => import("./form/Form096/Form096"));
const Form097 = React.lazy(() => import("./form/Form097/Form097"));
const Form098 = React.lazy(() => import("./form/Form098/Form098"));
const Form099 = React.lazy(() => import("./form/Form099/Form099"));
const Form100 = React.lazy(() => import("./form/Form100/Form100"));
const Form101 = React.lazy(() => import("./form/Form101/Form101"));
const Form102 = React.lazy(() => import("./form/Form102/Form102"));
const Form103 = React.lazy(() => import("./form/Form103/Form103"));
const Form104 = React.lazy(() => import("./form/Form104/Form104"));
const Form105 = React.lazy(() => import("./form/Form105/Form105"));
const Form106 = React.lazy(() => import("./form/Form106/Form106"));
const Form107 = React.lazy(() => import("./form/Form107/Form107"));
const Form107Management = React.lazy(() =>
    import("./form/Form107/Form107Management")
);
const Form001Approval = React.lazy(() => import("./approval/Form001Approval"));
const Form002Approval = React.lazy(() => import("./approval/Form002Approval"));
const Form003Approval = React.lazy(() => import("./approval/Form003Approval"));
const Form004Approval = React.lazy(() => import("./approval/Form004Approval"));
const Form005Approval = React.lazy(() => import("./approval/Form005Approval"));
const Form006Approval = React.lazy(() => import("./approval/Form006Approval"));
const Form007Approval = React.lazy(() => import("./approval/Form007Approval"));
const Form008Approval = React.lazy(() => import("./approval/Form008Approval"));
const Form009Approval = React.lazy(() => import("./approval/Form009Approval"));
const Form010Approval = React.lazy(() => import("./approval/Form010Approval"));
const Form011Approval = React.lazy(() => import("./approval/Form011Approval"));
const Form012Approval = React.lazy(() => import("./approval/Form012Approval"));
const Form013Approval = React.lazy(() => import("./approval/Form013Approval"));
const Form014Approval = React.lazy(() => import("./approval/Form014Approval"));
const Form015Approval = React.lazy(() => import("./approval/Form015Approval"));
const Form016Approval = React.lazy(() => import("./approval/Form016Approval"));
const Form017Approval = React.lazy(() => import("./approval/Form017Approval"));
const Form018Approval = React.lazy(() => import("./approval/Form018Approval"));
const Form019Approval = React.lazy(() => import("./approval/Form019Approval"));
const Form021Approval = React.lazy(() => import("./approval/Form021Approval"));
const Form022Approval = React.lazy(() => import("./approval/Form022Approval"));
const Form023Approval = React.lazy(() => import("./approval/Form023Approval"));
const Form026Approval = React.lazy(() => import("./approval/Form026Approval"));
const Form027Approval = React.lazy(() => import("./approval/Form027Approval"));
const Form028Approval = React.lazy(() => import("./approval/Form028Approval"));
const Form029Approval = React.lazy(() => import("./approval/Form029Approval"));
const Form030Approval = React.lazy(() => import("./approval/Form030Approval"));
const Form031Approval = React.lazy(() => import("./approval/Form031Approval"));
const Form032Approval = React.lazy(() => import("./approval/Form032Approval"));
const Form033Approval = React.lazy(() => import("./approval/Form033Approval"));
const Form034Approval = React.lazy(() => import("./approval/Form034Approval"));
const Form035Approval = React.lazy(() => import("./approval/Form035Approval"));
const Form040Approval = React.lazy(() => import("./approval/Form040Approval"));
const Form041Approval = React.lazy(() => import("./approval/Form041Approval"));
const Form042Approval = React.lazy(() => import("./approval/Form042Approval"));
const Form043Approval = React.lazy(() => import("./approval/Form043Approval"));
const Form044Approval = React.lazy(() => import("./approval/Form044Approval"));
const Form045Approval = React.lazy(() => import("./approval/Form045Approval"));
const Form046Approval = React.lazy(() => import("./approval/Form046Approval"));
const Form047Approval = React.lazy(() => import("./approval/Form047Approval"));
const Form048Approval = React.lazy(() => import("./approval/Form048Approval"));
const Form049Approval = React.lazy(() => import("./approval/Form049Approval"));
const Form050Approval = React.lazy(() => import("./approval/Form050Approval"));
const Form051Approval = React.lazy(() => import("./approval/Form051Approval"));
const Form052Approval = React.lazy(() => import("./approval/Form052Approval"));
const Form053Approval = React.lazy(() => import("./approval/Form053Approval"));
const Form054Approval = React.lazy(() => import("./approval/Form054Approval"));
const Form057Approval = React.lazy(() => import("./approval/Form057Approval"));
const Form058Approval = React.lazy(() => import("./approval/Form058Approval"));
const Form059Approval = React.lazy(() => import("./approval/Form059Approval"));
const Form062Approval = React.lazy(() => import("./approval/Form062Approval"));
const Form063Approval = React.lazy(() => import("./approval/Form063Approval"));
const Form064Approval = React.lazy(() => import("./approval/Form064Approval"));
const Form065Approval = React.lazy(() => import("./approval/Form065Approval"));
const Form066Approval = React.lazy(() => import("./approval/Form066Approval"));
const Form067Approval = React.lazy(() => import("./approval/Form067Approval"));
const Form068Approval = React.lazy(() => import("./approval/Form068Approval"));
const Form069Approval = React.lazy(() => import("./approval/Form069Approval"));
const Form070Approval = React.lazy(() => import("./approval/Form070Approval"));
const Form071Approval = React.lazy(() => import("./approval/Form071Approval"));
const Form072Approval = React.lazy(() => import("./approval/Form072Approval"));
const Form073Approval = React.lazy(() => import("./approval/Form073Approval"));
const Form074Approval = React.lazy(() => import("./approval/Form074Approval"));
const Form075Approval = React.lazy(() => import("./approval/Form075Approval"));
const Form076Approval = React.lazy(() => import("./approval/Form076Approval"));
const Form078Approval = React.lazy(() => import("./approval/Form078Approval"));
const Form079Approval = React.lazy(() => import("./approval/Form079Approval"));
const Form080Approval = React.lazy(() => import("./approval/Form080Approval"));
const Form081Approval = React.lazy(() => import("./approval/Form081Approval"));
const Form082Approval = React.lazy(() => import("./approval/Form082Approval"));
const Form083Approval = React.lazy(() => import("./approval/Form083Approval"));
const Form084Approval = React.lazy(() => import("./approval/Form084Approval"));
const Form085Approval = React.lazy(() => import("./approval/Form085Approval"));
const Form086Approval = React.lazy(() => import("./approval/Form086Approval"));
const Form087Approval = React.lazy(() => import("./approval/Form087Approval"));
const Form088Approval = React.lazy(() => import("./approval/Form088Approval"));
const Form089Approval = React.lazy(() => import("./approval/Form089Approval"));
const Form090Approval = React.lazy(() => import("./approval/Form090Approval"));
const Form091Approval = React.lazy(() => import("./approval/Form091Approval"));
const Form092Approval = React.lazy(() => import("./approval/Form092Approval"));
const Form093Approval = React.lazy(() => import("./approval/Form093Approval"));
const Form094Approval = React.lazy(() => import("./approval/Form094Approval"));
const Form095Approval = React.lazy(() => import("./approval/Form095Approval"));
const Form096Approval = React.lazy(() => import("./approval/Form096Approval"));
const Form097Approval = React.lazy(() => import("./approval/Form097Approval"));
const Form098Approval = React.lazy(() => import("./approval/Form098Approval"));
const Form099Approval = React.lazy(() => import("./approval/Form099Approval"));
const Form100Approval = React.lazy(() => import("./approval/Form100Approval"));
const Form101Approval = React.lazy(() => import("./approval/Form101Approval"));
const Form102Approval = React.lazy(() => import("./approval/Form102Approval"));
const Form103Approval = React.lazy(() => import("./approval/Form103Approval"));
const Form104Approval = React.lazy(() => import("./approval/Form104Approval"));
const Form105Approval = React.lazy(() => import("./approval/Form105Approval"));
const Form106Approval = React.lazy(() => import("./approval/Form106Approval"));
const Form107Approval = React.lazy(() => import("./approval/Form107Approval"));
const Form107Draft = React.lazy(() => import("./approval/Form107Draft"));
function App() {
    //change Date.prototype.toJSON for final submitting
    Date.prototype.toJSON = function () {
        return moment(this).tz("Asia/Hong_Kong").format();
        //return moment(this).format();
    };

    //for changing moment language
    const { t, i18n } = useTranslation();

    const SampleForm = [
        {
            path: "sample/DatePickerSampleForm",
            element: <DatePickerSampleForm />,
        },
        {
            path: "sample/TableInputSampleForm",
            element: <TableInputSampleForm />,
        },
        { path: "/sample/InputSampleForm", element: <InputSampleForm /> },
        {
            path: "/sample/MultiColumnSampleForm",
            element: <MultiColumnSampleForm />,
        },
    ];

    setLocale({
        number: {
            min: (min) => t("errorMsg.number.min", min),
            max: (max) => t("errorMsg.number.max", max),
        },
        string: {
            min: (min) => t("errorMsg.string.min", min),
            max: (max) => t("errorMsg.number.max", max),
        },
    });

    const theme = createTheme({
        palette: {
            primary: {
                main: "#A74128",
                //main: "#D05B3E",
                grey: "#ccc",
                dark: "#F4D4AB",
                // dim: "#742e1c",
                dim: "#300101",
            },
            secondary: {
                main: "#6C757D",
                light: "#FFEEDC",
                // dim: "#F4D4AB",
                //light: "#FFE4C7",
                dim: "#C37118",
            },
            tnc: {
                main: "#F8F9FA",
                secondary: "#E8E3CD",
            },
            iamSmart: {
                main: "#2B7366",
            },
            repeatInput: {
                main: "#f7f7f7",
                secondary: "#e5e5e5",
            },
            danger: {
                main: "#DC3545",
            },
            error: {
                main: "#9f3a38",
            },
            addBtn: {
                main: "#C54E30",
                hover: "#F4D4AB",
            },
            removeBtn: {},
        },
        spacing: 2,
        breakpoints: {
            values: {
                xs: 0,
                sm: 600,
                smd: 900,
                md: 960,
                lg: 1280,
                xl: 1920,
            },
        },
        overrides: {
            MuiInputLabel: {
                root: {
                    // display: 'flex',
                    color: "#333",
                    marginBottom: "4px",
                },
                formControl: {
                    position: "relative",
                    display: "flex",
                    alignItems: "center",
                },
                shrink: {
                    transform: "translate(0, 1.5px)",
                },
            },
            MuiInput: {
                formControl: {
                    "label + &": {
                        marginTop: "0",
                    },
                },
            },
            MuiButton: {
                root: {
                    borderRadius: 0,
                    "&:hover": {
                        backgroundColor: "primary.dark",
                    },
                },
            },
            MuiBackdrop: {
                root: {
                    zIndex: 0,
                },
            },
        },
    });

    const dispatch = useDispatch();
    const { currentStep, fieldValues, formLanguage } = useSelector(
        getFormTemp()
    );
    const reloadOpen = useSelector(getPopupStatus("reload"));
    const loadFormOpen = useSelector(getPopupStatus("loadForm"));
    const isLoading = useSelector(getLoading());
    const [cookies, setCookie, removeCookie] = useCookies(["token"]);
    const loggedIn = isNotEmpty(cookies?.token);
    const token = loggedIn ? cookies.token : "";
    const selectedUserOrg = useSelector(getSelectedUserOrg());
    const tokenInRedux = useSelector(getToken());
    const serverTime = useSelector(getServerTime());
    const role = useSelector(getUserRole());

    const isIamsmartCallbackPage = () => {
        const pathname = window.location?.pathname || "";
        const search = window.location?.search || "";
        const params = new URLSearchParams(search);
        return (
            pathname.includes("/iamsmartLogin") &&
            (params.has("RefID") || params.has("code"))
        );
    };

    useEffect(() => {
        if (isIamsmartCallbackPage()) {
            return;
        }
        if (isObjectNotEmpty(cookies) && loggedIn) {
            moment.tz.setDefault("Asia/Hong_Kong");
            const decodedJwt = jwt_decode(token);
            const expDate = moment
                .utc(decodedJwt.exp * 1000)
                .toDate()
                .getTime();
            const currntTime = serverTime
                ? moment(serverTime).toDate().getTime()
                : moment().toDate().getTime();
            if (currntTime < expDate) {
                dispatch(parseToken({ token: cookies.token }));
            } else {
                console.log(
                    "Your JWT is expired. You have been automatically logged out."
                );
                removeCookie("token", {
                    path: `${Config.CONTENT_PATH}`,
                });
                if (
                    navigator.userAgent.includes("iAM Smart In-App Browser") &&
                    window.eIDJSBridge
                ) {
                    window.eIDJSBridge.closeInAppBrowser();
                } else {
                    dispatch(logout());
                }
            }
        } else {
            dispatch(initAnon());
        }
    }, [token]);
    //refresh jwt token
    useEffect(() => {
        if (isNotEmpty(tokenInRedux)) {
            const decodedJwt = jwt_decode(tokenInRedux);
            const expDate = moment
                .utc(decodedJwt.exp * 1000)
                .toDate()
                .getTime();
            const currntTime = serverTime
                ? moment(serverTime).toDate().getTime()
                : moment().toDate().getTime();
            if (currntTime < expDate) {
                const refreshTime = expDate - currntTime - 3 * 60 * 1000;
                /*console.log(
                    "token expired at: ",
                    moment(expDate)
                );
                console.log(
                    "will refresh token at: ",
                    moment(new Date().getTime() + refreshTime)
                );*/
                let refreshTokenTimeout = setTimeout(() => {
                    if (!isTimeout) {
                        dispatch(parseToken({ token: tokenInRedux }));
                    }
                }, refreshTime); //refresh token 1 minutes before the expiration
                return () => {
                    clearTimeout(refreshTokenTimeout);
                };
            }
        }
    }, [tokenInRedux]);
    useEffect(() => {
        if (isNotEmpty(token)) {
            dispatch(getFormTitleParam(selectedUserOrg));
            //console.log("UpdateAccessRoleinHeader:", selectedUserOrg);
            if (isObjectNotEmpty(selectedUserOrg)) {
                dispatch(updateProfile(selectedUserOrg.scrn));
                dispatch(updateUserRole(selectedUserOrg.scrn));
                dispatch(getCloParameter(selectedUserOrg.scrn));
                dispatch(getSelectFormList(selectedUserOrg)); //fetch for applicant form list when org. change
            }
        }
    }, [selectedUserOrg]);

    const loggedInRoles = [
        "SA", //System admin
        "FO", //Form Owner (EDB)
        "FR", //Form Reviewer (EDB)
        "SLA", //Local Admin of SSLO
        "CLA", //Local Admin of ClO
        "CH", //Head of CLO
        "SH", //Head of SSLO
        "C", //CLO user without special role
        "E", //EDB user without special role (EDB)
        "S", //SSLO user without special role
        "Public",
    ];

    const rolesExceptSANEdb = ["SLA", "CLA", "CH", "SH", "C", "S", "Public"];
    const rolesExceptSA = [
        "FO",
        "FR",
        "SLA",
        "CLA",
        "CH",
        "SH",
        "C",
        "S",
        "E",
        "Public",
    ];
    const rolesExceptE = [
        "SA", //System admin
        "FO", //Form Owner
        "FR", //Form Reviewer
        "SLA", //Local Admin of SSLO
        "CLA", //Local Admin of ClO
        "CH", //Head of CLO
        "SH", //Head of SSLO
        "C", //CLO user without special role
        "S", //SSLO user without special role
        "Public",
    ];
    const isTimeout = useTimeout();
    const contentPath = Config.CONTENT_PATH.split("/")[1];

    return (
        <ThemeProvider theme={theme}>
            {isTimeout && <TimeoutDialog />}
            <AlertDialog
                formOpen={reloadOpen}
                content={t("common.reload.popup.msg")}
                confirmButton={{
                    action: () => {
                        if (
                            navigator.userAgent.includes(
                                "iAM Smart In-App Browser"
                            ) &&
                            window.eIDJSBridge
                        ) {
                            window.eIDJSBridge.closeInAppBrowser();
                        } else {
                            removeCookie("token", {
                                path: `${Config.CONTENT_PATH}`,
                            });
                            window.location.reload();
                        }
                    },
                    label: t("common.reload.popup.confirm"),
                }}
            />
            <AlertDialog
                formOpen={loadFormOpen}
                content={t("common.loadForm.popup.msg")}
                confirmButton={{
                    action: () => {
                        dispatch(setFullFieldValues(fieldValues));
                        dispatch(setCurrentStepWithRerender(currentStep));
                        dispatch(toggleLoadFormPopup());
                        i18n.changeLanguage(formLanguage);
                    },
                    label: t("common.loadForm.popup.confirm"),
                }}
                closeButton={{
                    action: () => dispatch(toggleLoadFormPopup()),
                    label: t("common.loadForm.popup.cancel"),
                }}
            />
            <Loading open={isLoading} />
            <HistoryRouter basename={contentPath} history={history}>
                <Suspense fallback={<Loading />}>
                    <AuthProvider>
                        <Routes>
                            <Route
                                path="/"
                                element={
                                    <>
                                        <Header />
                                        <Navigate to="/Login" />
                                    </>
                                }
                            />
                            <Route
                                path="/index.html"
                                element={
                                    <>
                                        <Header />
                                        <Navigate to="/Login" />
                                    </>
                                }
                            />
                            <Route
                                path="select"
                                element={
                                    <Auth roles={rolesExceptSA} annoAccess>
                                        <FormSelector />
                                    </Auth>
                                }
                            />
                            {/* <Route path="GuidanceNotes" element={<GuidanceNotes />} /> */}
                            <Route
                                path="OnlineTutorial"
                                element={<OnlineTutorial />}
                            />
                            <Route path="Faq" element={<Faq />} />
                            <Route path="Enquiry" element={<Enquiry />} />
                            <Route
                                path="MaintenanceTime"
                                element={<MaintenanceTime />}
                            />
                            <Route
                                path="iamsmart"
                                element={<Authentication />}
                            />
                            <Route
                                path="Form001"
                                element={
                                    <FormGuard formId={"Form001"}>
                                        <Form001 />
                                    </FormGuard>
                                }
                            />
                            <Route
                                path="Form008"
                                element={
                                    <Auth byList annoAccess>
                                        <Form008 />
                                    </Auth>
                                }
                            />
                            <Route
                                path="Form009"
                                element={
                                    <Auth byList annoAccess>
                                        <Form009 />
                                    </Auth>
                                }
                            />
                            <Route
                                path="Form010"
                                element={
                                    <Auth byList annoAccess>
                                        <Form010 />
                                    </Auth>
                                }
                            />
                            <Route
                                path="Form011"
                                element={
                                    <Auth byList annoAccess>
                                        <Form011 />
                                    </Auth>
                                }
                            />
                            <Route
                                path="Form012"
                                element={
                                    <Auth byList annoAccess>
                                        <Form012 />
                                    </Auth>
                                }
                            />
                            <Route
                                path="Form013"
                                element={
                                    <Auth byList annoAccess>
                                        <Form013 />
                                    </Auth>
                                }
                            />
                            <Route
                                path="Form016"
                                element={
                                    <Auth byList annoAccess>
                                        <Form016 />
                                    </Auth>
                                }
                            />
                            <Route
                                path="Form017"
                                element={
                                    <Auth byList annoAccess>
                                        <Form017 />
                                    </Auth>
                                }
                            />
                            <Route
                                path="Form018"
                                element={
                                    <Auth byList annoAccess>
                                        <Form018 />
                                    </Auth>
                                }
                            />
                            <Route
                                path="Form019"
                                element={
                                    <Auth byList annoAccess>
                                        <Form019 />
                                    </Auth>
                                }
                            />
                            <Route
                                path="Form021"
                                element={
                                    <Auth byList annoAccess>
                                        <Form021 />
                                    </Auth>
                                }
                            />
                            <Route
                                path="Form022"
                                element={
                                    <Auth byList annoAccess>
                                        <Form022 />
                                    </Auth>
                                }
                            />
                            <Route
                                path="Form023"
                                element={
                                    <Auth byList annoAccess>
                                        <Form023 />
                                    </Auth>
                                }
                            />
                            <Route
                                path="Form002"
                                element={
                                    <Auth byList annoAccess>
                                        <Form002 />
                                    </Auth>
                                }
                            />
                            <Route
                                path="Form003"
                                element={
                                    <Auth byList annoAccess>
                                        <Form003 />
                                    </Auth>
                                }
                            />
                            <Route
                                path="Form004"
                                element={
                                    <Auth byList annoAccess>
                                        <Form004 />
                                    </Auth>
                                }
                            />
                            <Route
                                path="Form005"
                                element={
                                    <Auth byList annoAccess>
                                        <Form005 />
                                    </Auth>
                                }
                            />
                            <Route
                                path="Form006"
                                element={
                                    <Auth byList annoAccess>
                                        <Form006 />
                                    </Auth>
                                }
                            />
                            <Route
                                path="Form007"
                                element={
                                    <Auth byList annoAccess>
                                        <Form007 />
                                    </Auth>
                                }
                            />
                            <Route
                                path="Form014"
                                element={
                                    <Auth byList annoAccess>
                                        <Form014 />
                                    </Auth>
                                }
                            />
                            <Route
                                path="Form015"
                                element={
                                    <Auth byList annoAccess>
                                        <Form015 />
                                    </Auth>
                                }
                            />
                            <Route
                                path="Form026"
                                element={
                                    <Auth byList annoAccess>
                                        <Form026 />
                                    </Auth>
                                }
                            />
                            <Route
                                path="Form027"
                                element={
                                    <Auth byList annoAccess>
                                        <Form027 />
                                    </Auth>
                                }
                            />
                            <Route
                                path="Form028"
                                element={
                                    <Auth byList annoAccess>
                                        <Form028 />
                                    </Auth>
                                }
                            />
                            <Route
                                path="Form029"
                                element={
                                    <Auth byList annoAccess>
                                        <Form029 />
                                    </Auth>
                                }
                            />
                            <Route
                                path="Form030"
                                element={
                                    <Auth byList annoAccess>
                                        <Form030 />
                                    </Auth>
                                }
                            />
                            <Route
                                path="Form031"
                                element={
                                    <Auth byList annoAccess>
                                        <Form031 />
                                    </Auth>
                                }
                            />
                            <Route
                                path="Form032"
                                element={
                                    <Auth byList annoAccess>
                                        <Form032 />
                                    </Auth>
                                }
                            />
                            <Route
                                path="Form033"
                                element={
                                    <Auth byList annoAccess>
                                        <Form033 />
                                    </Auth>
                                }
                            />
                            <Route
                                path="Form034"
                                element={
                                    <Auth byList annoAccess>
                                        <Form034 />
                                    </Auth>
                                }
                            />
                            <Route
                                path="Form035"
                                element={
                                    <Auth byList annoAccess>
                                        <Form035 />
                                    </Auth>
                                }
                            />
                            <Route
                                path="Form050"
                                element={
                                    <Auth byList annoAccess>
                                        <Form050 />
                                    </Auth>
                                }
                            />
                            <Route
                                path="Form052"
                                element={
                                    <Auth byList annoAccess>
                                        <Form052 />
                                    </Auth>
                                }
                            />
                            <Route
                                path="Form073"
                                element={
                                    <Auth byList annoAccess>
                                        <Form073 />
                                    </Auth>
                                }
                            />
                            <Route
                                path="Form074"
                                element={
                                    <Auth byList annoAccess>
                                        <Form074 />
                                    </Auth>
                                }
                            />
                            <Route
                                path="Form075"
                                element={
                                    <Auth byList annoAccess>
                                        <Form075 />
                                    </Auth>
                                }
                            />
                            <Route
                                path="Form076"
                                element={
                                    <Auth byList annoAccess>
                                        <Form076 />
                                    </Auth>
                                }
                            />
                            <Route
                                path="Form078"
                                element={
                                    <Auth byList annoAccess>
                                        <Form078 />
                                    </Auth>
                                }
                            />
                            <Route
                                path="Form079"
                                element={
                                    <Auth byList annoAccess>
                                        <Form079 />
                                    </Auth>
                                }
                            />
                            <Route
                                path="Form080"
                                element={
                                    <Auth byList annoAccess>
                                        <Form080 />
                                    </Auth>
                                }
                            />
                            <Route
                                path="Form081"
                                element={
                                    <Auth byList annoAccess>
                                        <Form081 />
                                    </Auth>
                                }
                            />
                            <Route
                                path="Form082"
                                element={
                                    <Auth byList annoAccess>
                                        <Form082 />
                                    </Auth>
                                }
                            />
                            <Route
                                path="Form083"
                                element={
                                    <Auth byList annoAccess>
                                        <Form083 />
                                    </Auth>
                                }
                            />
                            <Route
                                path="Form084"
                                element={
                                    <Auth byList annoAccess>
                                        <Form084 />
                                    </Auth>
                                }
                            />
                            <Route
                                path="Form090"
                                element={
                                    <Auth byList annoAccess>
                                        <Form090 />
                                    </Auth>
                                }
                            />
                            <Route
                                path="Form091"
                                element={
                                    <Auth byList annoAccess>
                                        <Form091 />
                                    </Auth>
                                }
                            />
                            <Route
                                path="Form092"
                                element={
                                    <Auth byList annoAccess>
                                        <Form092 />
                                    </Auth>
                                }
                            />
                            <Route
                                path="Form093"
                                element={
                                    <Auth byList annoAccess>
                                        <Form093 />
                                    </Auth>
                                }
                            />
                            <Route
                                path="Form094"
                                element={
                                    <Auth byList annoAccess>
                                        <Form094 />
                                    </Auth>
                                }
                            />
                            <Route
                                path="Form095"
                                element={
                                    <Auth byList annoAccess>
                                        <Form095 />
                                    </Auth>
                                }
                            />
                            <Route
                                path="Form096"
                                element={
                                    <Auth byList annoAccess>
                                        <Form096 />
                                    </Auth>
                                }
                            />
                            <Route
                                path="Form097"
                                element={
                                    <Auth byList annoAccess>
                                        <Form097 />
                                    </Auth>
                                }
                            />
                            <Route
                                path="Form098"
                                element={
                                    <Auth byList annoAccess>
                                        <Form098 />
                                    </Auth>
                                }
                            />
                            <Route
                                path="Form099"
                                element={
                                    <Auth byList annoAccess>
                                        <Form099 />
                                    </Auth>
                                }
                            />
                            <Route
                                path="Form100"
                                element={
                                    <Auth byList annoAccess>
                                        <Form100 />
                                    </Auth>
                                }
                            />
                            <Route
                                path="Form101"
                                element={
                                    <Auth byList annoAccess>
                                        <Form101 />
                                    </Auth>
                                }
                            />
                            <Route
                                path="Form102"
                                element={
                                    <Auth byList annoAccess>
                                        <Form102 />
                                    </Auth>
                                }
                            />
                            <Route
                                path="Form103"
                                element={
                                    <Auth byList annoAccess>
                                        <Form103 />
                                    </Auth>
                                }
                            />
                            <Route
                                path="Form104"
                                element={
                                    <Auth byList annoAccess>
                                        <Form104 />
                                    </Auth>
                                }
                            />
                            <Route
                                path="Form105"
                                element={
                                    <Auth byList annoAccess>
                                        <Form105 />
                                    </Auth>
                                }
                            />
                            <Route
                                path="Form106"
                                element={
                                    <Auth byList annoAccess>
                                        <Form106 />
                                    </Auth>
                                }
                            />
                            <Route
                                path="Form107"
                                element={
                                    <Auth byList annoAccess>
                                        <Form107 />
                                    </Auth>
                                }
                            />
                            <Route
                                path="Form107DraftManage"
                                element={
                                    <Auth byList annoAccess>
                                        <Form107Management />
                                    </Auth>
                                }
                            />
                            <Route
                                path="draft/Form107/:referenceId"
                                element={
                                    <Auth roles={loggedInRoles}>
                                        <Form107Draft />
                                    </Auth>
                                }
                            />
                            <Route
                                path="Form040"
                                element={
                                    <Auth byList annoAccess>
                                        <Form040 />
                                    </Auth>
                                }
                            />
                            <Route
                                path="Form041"
                                element={
                                    <Auth byList annoAccess>
                                        <Form041 />
                                    </Auth>
                                }
                            />
                            <Route
                                path="Form042"
                                element={
                                    <Auth byList annoAccess>
                                        <Form042 />
                                    </Auth>
                                }
                            />
                            <Route
                                path="Form043"
                                element={
                                    <Auth byList annoAccess>
                                        <Form043 />
                                    </Auth>
                                }
                            />
                            <Route
                                path="Form044"
                                element={
                                    <Auth byList annoAccess>
                                        <Form044 />
                                    </Auth>
                                }
                            />
                            <Route
                                path="Form045"
                                element={
                                    <Auth byList annoAccess>
                                        <Form045 />
                                    </Auth>
                                }
                            />
                            <Route
                                path="Form046"
                                element={
                                    <Auth byList annoAccess>
                                        <Form046 />
                                    </Auth>
                                }
                            />
                            <Route
                                path="Form047"
                                element={
                                    <Auth byList annoAccess>
                                        <Form047 />
                                    </Auth>
                                }
                            />
                            <Route
                                path="Form048"
                                element={
                                    <Auth byList annoAccess>
                                        <Form048 />
                                    </Auth>
                                }
                            />
                            <Route
                                path="Form049"
                                element={
                                    <Auth byList annoAccess>
                                        <Form049 />
                                    </Auth>
                                }
                            />
                            <Route
                                path="Form051"
                                element={
                                    <Auth byList annoAccess>
                                        <Form051 />
                                    </Auth>
                                }
                            />
                            <Route
                                path="Form053"
                                element={
                                    <Auth byList annoAccess>
                                        <Form053 />
                                    </Auth>
                                }
                            />
                            <Route
                                path="Form054"
                                element={
                                    <Auth byList annoAccess>
                                        <Form054 />
                                    </Auth>
                                }
                            />
                            <Route
                                path="Form057"
                                element={
                                    <Auth byList annoAccess>
                                        <Form057 />
                                    </Auth>
                                }
                            />
                            <Route
                                path="Form058"
                                element={
                                    <Auth byList annoAccess>
                                        <Form058 />
                                    </Auth>
                                }
                            />
                            <Route
                                path="Form059"
                                element={
                                    <Auth byList annoAccess>
                                        <Form059 />
                                    </Auth>
                                }
                            />
                            <Route
                                path="Form062"
                                element={
                                    <Auth byList annoAccess>
                                        <Form062 />
                                    </Auth>
                                }
                            />
                            <Route
                                path="Form063"
                                element={
                                    <Auth byList annoAccess>
                                        <Form063 />
                                    </Auth>
                                }
                            />
                            <Route
                                path="Form064"
                                element={
                                    <Auth byList annoAccess>
                                        <Form064 />
                                    </Auth>
                                }
                            />
                            <Route
                                path="Form065"
                                element={
                                    <Auth byList annoAccess>
                                        <Form065 />
                                    </Auth>
                                }
                            />
                            <Route
                                path="Form066"
                                element={
                                    <Auth byList annoAccess>
                                        <Form066 />
                                    </Auth>
                                }
                            />
                            <Route
                                path="Form067"
                                element={
                                    <Auth byList annoAccess>
                                        <Form067 />
                                    </Auth>
                                }
                            />
                            <Route
                                path="Form068"
                                element={
                                    <Auth byList annoAccess>
                                        <Form068 />
                                    </Auth>
                                }
                            />
                            <Route
                                path="Form069"
                                element={
                                    <Auth byList annoAccess>
                                        <Form069 />
                                    </Auth>
                                }
                            />
                            <Route
                                path="Form070"
                                element={
                                    <Auth byList annoAccess>
                                        <Form070 />
                                    </Auth>
                                }
                            />
                            <Route
                                path="Form071"
                                element={
                                    <Auth byList annoAccess>
                                        <Form071 />
                                    </Auth>
                                }
                            />
                            <Route
                                path="Form072"
                                element={
                                    <Auth byList annoAccess>
                                        <Form072 />
                                    </Auth>
                                }
                            />
                            <Route
                                path="Form085"
                                element={
                                    <Auth byList annoAccess>
                                        <Form085 />
                                    </Auth>
                                }
                            />
                            <Route
                                path="Form086"
                                element={
                                    <Auth byList annoAccess>
                                        <Form086 />
                                    </Auth>
                                }
                            />
                            <Route
                                path="Form087"
                                element={
                                    <Auth byList annoAccess>
                                        <Form087 />
                                    </Auth>
                                }
                            />
                            <Route
                                path="Form088"
                                element={
                                    <Auth byList annoAccess>
                                        <Form088 />
                                    </Auth>
                                }
                            />
                            <Route
                                path="Form089"
                                element={
                                    <Auth byList annoAccess>
                                        <Form089 />
                                    </Auth>
                                }
                            />
                            <Route
                                path="Login"
                                element={loggedIn ? <Main /> : <Login />}
                            />
                            <Route
                                path="edbuserlogin"
                                element={loggedIn ? <Main /> : <Login edb />}
                            />
                            <Route
                                path="main"
                                element={
                                    loggedIn ? (
                                        <Main />
                                    ) : (
                                        <Navigate to="/Login" />
                                    )
                                }
                            />
                            <Route
                                path="main/:token"
                                element={
                                    loggedIn ? (
                                        <Main />
                                    ) : (
                                        <Navigate to="/Login" />
                                    )
                                }
                            />
                            <Route path="loginCLO" element={<LoginCLO />} />
                            <Route path="loginSSLO" element={<LoginSSLO />} />
                            <Route
                                path="loginPublic"
                                element={<LoginPublic />}
                            />
                            <Route
                                path="afterLoginCLO"
                                element={<AfterLoginCLO />}
                            />
                            <Route
                                path="loginIAMSmart"
                                element={<LoginIAMSmart />}
                            />
                            <Route
                                path="iamsmartLogin"
                                element={<IamsmartLogin />}
                            />
                            <Route
                                path="iamsmartAnonFormFilling"
                                element={<IamsmartAnonFormFilling />}
                            />
                            <Route
                                path="iamsmartAnonSigning"
                                element={<IamsmartAnonSigning />}
                            />
                            <Route
                                path="ParameterPage"
                                element={
                                    <Auth roles={["FO"]}>
                                        <ParameterPage />
                                    </Auth>
                                }
                            />
                            <Route
                                path="CCCUpdatePage"
                                element={
                                    <Auth roles={["FO"]}>
                                        <CCCUpdatePage />
                                    </Auth>
                                }
                            />
                            <Route
                                path="ProfilePage"
                                element={
                                    <Auth roles={loggedInRoles}>
                                        <ProfilePage />
                                    </Auth>
                                }
                            />
                            <Route
                                path="DelegationPage"
                                element={
                                    <Auth roles={["SLA", "CLA", "CH", "SH"]}>
                                        <DelegationPage />
                                    </Auth>
                                }
                            />
                            <Route
                                path="FormEnquiry"
                                element={
                                    <Auth
                                        roles={
                                            getIsHideSearchPageRoles(role)
                                                ? loggedInRoles?.filter(
                                                      (loggedInRole) =>
                                                          loggedInRole !== role
                                                  )
                                                : loggedInRoles
                                        }
                                    >
                                        <FormEnquiry />
                                    </Auth>
                                }
                            />
                            <Route
                                path="Reminder"
                                element={
                                    <Auth roles={loggedInRoles}>
                                        <EditYear />
                                    </Auth>
                                }
                            />
                            <Route
                                path="EditStaff"
                                element={
                                    <Auth roles={["SLA", "CLA"]}>
                                        <EditStaff />
                                    </Auth>
                                }
                            />
                            <Route
                                path="EditOrgEdb"
                                element={
                                    <Auth roles={["SA"]}>
                                        <EditOrgEdb />
                                    </Auth>
                                }
                            />
                            <Route
                                path="EditPostSecondary"
                                element={
                                    <Auth roles={["SA"]}>
                                        <EditPostSecondary />
                                    </Auth>
                                }
                            />
                            <Route
                                path="EditReviewerEdb"
                                element={
                                    <Auth roles={["FO"]}>
                                        <EditReviewerEdb />
                                    </Auth>
                                }
                            />
                            <Route
                                path="EditPurpose"
                                element={
                                    <Auth roles={["FO", "FR", "E", "SA"]}>
                                        <EditPurpose />
                                    </Auth>
                                }
                            />
                            <Route
                                path="EditFormOwner"
                                element={
                                    <Auth roles={["SA"]}>
                                        <EditFormOwner />
                                    </Auth>
                                }
                            />
                            <Route
                                path="SystemMessage"
                                element={
                                    <Auth roles={["SA"]}>
                                        <SystemMessage />
                                    </Auth>
                                }
                            />
                            <Route
                                path="forgotPassword"
                                element={<ForgotPassword />}
                            />
                            <Route
                                path="changePassword"
                                element={
                                    <Auth
                                        roles={[
                                            "SA",
                                            "SLA",
                                            "SH",

                                            "S",
                                            "notLoggedIn",
                                        ]}
                                    >
                                        <ChangePassword />
                                    </Auth>
                                }
                            />

                            <Route
                                path="HistoryEnquiry"
                                element={
                                    <Auth
                                        roles={
                                            getIsHideSearchPageRoles(role)
                                                ? ["SA", "Nobody"]?.filter(
                                                      (loggedInRole) =>
                                                          loggedInRole !== role
                                                  )
                                                : ["SA"]
                                        }
                                    >
                                        <HistoryEnquiry />
                                    </Auth>
                                }
                            />

                            <Route
                                path="MonthlySubmissionReport"
                                element={
                                    <Auth roles={["SA"]}>
                                        <MonthlySubmissionReport />
                                    </Auth>
                                }
                            />

                            <Route
                                path="HealthCheck"
                                element={
                                    <Auth roles={["SA"]}>
                                        <HealthCheck />
                                    </Auth>
                                }
                            />

                            {SampleForm.map((props, index) => (
                                <Route key={index} {...props} />
                            ))}
                            <Route path="forbidden" element={<Forbidden />} />
                            <Route path="approval">
                                <Route
                                    path="Form001/:referenceId"
                                    element={
                                        <Auth roles={loggedInRoles}>
                                            <Form001Approval />
                                        </Auth>
                                    }
                                />
                                <Route
                                    path="Form002/:referenceId"
                                    element={
                                        <Auth
                                            roles={[
                                                "C",
                                                "CH",
                                                "FR",
                                                "FO",
                                                "SA",
                                                "CLA",
                                            ]}
                                        >
                                            <Form002Approval />
                                        </Auth>
                                    }
                                />
                                <Route
                                    path="Form003/:referenceId"
                                    element={
                                        <Auth
                                            roles={[
                                                "C",
                                                "CH",
                                                "FR",
                                                "FO",
                                                "SA",
                                                "CLA",
                                            ]}
                                        >
                                            <Form003Approval />
                                        </Auth>
                                    }
                                />
                                <Route
                                    path="Form004/:referenceId"
                                    element={
                                        <Auth
                                            roles={[
                                                "S",
                                                "SH",
                                                "SLA",
                                                "FR",
                                                "FO",
                                                "SA",
                                                "CLA",
                                            ]}
                                        >
                                            <Form004Approval />
                                        </Auth>
                                    }
                                />
                                <Route
                                    path="Form005/:referenceId"
                                    element={
                                        <Auth
                                            roles={[
                                                "S",
                                                "SH",
                                                "SLA",
                                                "FR",
                                                "FO",
                                                "SA",
                                                "CLA",
                                            ]}
                                        >
                                            <Form005Approval />
                                        </Auth>
                                    }
                                />
                                <Route
                                    path="Form006/:referenceId"
                                    element={
                                        <Auth
                                            roles={[
                                                "S",
                                                "SH",
                                                "FR",
                                                "FO",
                                                "SA",
                                                "CLA",
                                                "SLA",
                                            ]}
                                        >
                                            <Form006Approval />
                                        </Auth>
                                    }
                                />
                                <Route
                                    path="Form007/:referenceId"
                                    element={
                                        <Auth
                                            roles={[
                                                "C",
                                                "CH",
                                                "FR",
                                                "FO",
                                                "SA",
                                                "CLA",
                                            ]}
                                        >
                                            <Form007Approval />
                                        </Auth>
                                    }
                                />
                                <Route
                                    path="Form008/:referenceId"
                                    element={
                                        <Auth
                                            roles={[
                                                "Public",
                                                "C",
                                                "CH",
                                                "FR",
                                                "FO",
                                                "SA",
                                                "CLA",
                                            ]}
                                        >
                                            <Form008Approval />
                                        </Auth>
                                    }
                                />
                                <Route
                                    path="Form009/:referenceId"
                                    element={
                                        <Auth
                                            roles={[
                                                "C",
                                                "CH",
                                                "FR",
                                                "FO",
                                                "SA",
                                                "CLA",
                                            ]}
                                        >
                                            <Form009Approval />
                                        </Auth>
                                    }
                                />
                                <Route
                                    path="Form010/:referenceId"
                                    element={
                                        <Auth roles={loggedInRoles}>
                                            <Form010Approval />
                                        </Auth>
                                    }
                                />
                                <Route
                                    path="Form011/:referenceId"
                                    element={
                                        <Auth
                                            roles={[
                                                "C",
                                                "CH",
                                                "FR",
                                                "FO",
                                                "SA",
                                                "CLA",
                                            ]}
                                        >
                                            <Form011Approval />
                                        </Auth>
                                    }
                                />
                                <Route
                                    path="Form012/:referenceId"
                                    element={
                                        <Auth
                                            roles={[
                                                "CH",
                                                "S",
                                                "SH",
                                                "FR",
                                                "FO",
                                                "SA",
                                                "SLA",
                                                "CLA",
                                            ]}
                                        >
                                            <Form012Approval />
                                        </Auth>
                                    }
                                />
                                <Route
                                    path="Form013/:referenceId"
                                    element={
                                        <Auth
                                            roles={[
                                                "S",
                                                "SH",
                                                "FR",
                                                "FO",
                                                "SA",
                                                "SLA",
                                                "CLA",
                                            ]}
                                        >
                                            <Form013Approval />
                                        </Auth>
                                    }
                                />
                                <Route
                                    path="Form014/:referenceId"
                                    element={
                                        <Auth
                                            roles={[
                                                "C",
                                                "CH",
                                                "FR",
                                                "FO",
                                                "SA",
                                                "CLA",
                                            ]}
                                        >
                                            <Form014Approval />
                                        </Auth>
                                    }
                                />
                                <Route
                                    path="Form015/:referenceId"
                                    element={
                                        <Auth roles={rolesExceptE}>
                                            <Form015Approval />
                                        </Auth>
                                    }
                                />
                                <Route
                                    path="Form016/:referenceId"
                                    element={
                                        <Auth roles={rolesExceptE}>
                                            <Form016Approval />
                                        </Auth>
                                    }
                                />
                                <Route
                                    path="Form017/:referenceId"
                                    element={
                                        <Auth roles={rolesExceptE}>
                                            <Form017Approval />
                                        </Auth>
                                    }
                                />
                                <Route
                                    path="Form018/:referenceId"
                                    element={
                                        <Auth roles={rolesExceptE}>
                                            <Form018Approval />
                                        </Auth>
                                    }
                                />
                                <Route
                                    path="Form019/:referenceId"
                                    element={
                                        <Auth
                                            roles={[
                                                "C",
                                                "CH",
                                                "FR",
                                                "FO",
                                                "SA",
                                                "E",
                                                "CLA",
                                            ]}
                                        >
                                            <Form019Approval />
                                        </Auth>
                                    }
                                />
                                <Route
                                    path="Form021/:referenceId"
                                    element={
                                        <Auth
                                            roles={[
                                                "C",
                                                "CH",
                                                "FR",
                                                "FO",
                                                "SA",
                                                "E",
                                                "CLA",
                                            ]}
                                        >
                                            <Form021Approval />
                                        </Auth>
                                    }
                                />
                                <Route
                                    path="Form022/:referenceId"
                                    element={
                                        <Auth
                                            roles={[
                                                "C",
                                                "CH",
                                                "CLA",
                                                "FR",
                                                "FO",
                                                "SA",
                                                "CLA",
                                            ]}
                                        >
                                            <Form022Approval />
                                        </Auth>
                                    }
                                />
                                <Route
                                    path="Form023/:referenceId"
                                    element={
                                        <Auth
                                            roles={[
                                                "C",
                                                "CH",
                                                "CLA",
                                                "FR",
                                                "FO",
                                                "SA",
                                                "CLA",
                                            ]}
                                        >
                                            <Form023Approval />
                                        </Auth>
                                    }
                                />
                                <Route
                                    path="Form026/:referenceId"
                                    element={
                                        <Auth roles={rolesExceptE}>
                                            <Form026Approval />
                                        </Auth>
                                    }
                                />
                                <Route
                                    path="Form027/:referenceId"
                                    element={
                                        <Auth roles={rolesExceptE}>
                                            <Form027Approval />
                                        </Auth>
                                    }
                                />
                                <Route
                                    path="Form028/:referenceId"
                                    element={
                                        <Auth
                                            roles={[
                                                "C",
                                                "CH",
                                                "CLA",
                                                "FR",
                                                "FO",
                                                "SA",
                                                "Public",
                                                "CLA",
                                            ]}
                                        >
                                            <Form028Approval />
                                        </Auth>
                                    }
                                />
                                <Route
                                    path="Form029/:referenceId"
                                    element={
                                        <Auth roles={rolesExceptE}>
                                            <Form029Approval />
                                        </Auth>
                                    }
                                />
                                <Route
                                    path="Form030/:referenceId"
                                    element={
                                        <Auth
                                            roles={[
                                                "C",
                                                "CLA",
                                                "CH",
                                                "FR",
                                                "FO",
                                                "SA",
                                                "E",
                                                "CLA",
                                            ]}
                                        >
                                            <Form030Approval />
                                        </Auth>
                                    }
                                />
                                <Route
                                    path="Form031/:referenceId"
                                    element={
                                        <Auth
                                            roles={[
                                                "C",
                                                "CH",
                                                "FR",
                                                "FO",
                                                "SA",
                                                "E",
                                                "CLA",
                                            ]}
                                        >
                                            <Form031Approval />
                                        </Auth>
                                    }
                                />
                                <Route
                                    path="Form032/:referenceId"
                                    element={
                                        <Auth
                                            roles={[
                                                "C",
                                                "CH",
                                                "FR",
                                                "FO",
                                                "SA",
                                                "CLA",
                                            ]}
                                        >
                                            <Form032Approval />
                                        </Auth>
                                    }
                                />
                                <Route
                                    path="Form033/:referenceId"
                                    element={
                                        <Auth
                                            roles={[
                                                "C",
                                                "CH",
                                                "FR",
                                                "FO",
                                                "SA",
                                                "CLA",
                                            ]}
                                        >
                                            <Form033Approval />
                                        </Auth>
                                    }
                                />
                                <Route
                                    path="Form034/:referenceId"
                                    element={
                                        <Auth
                                            roles={[
                                                "C",
                                                "CH",
                                                "FR",
                                                "FO",
                                                "SA",
                                                "CLA",
                                            ]}
                                        >
                                            <Form034Approval />
                                        </Auth>
                                    }
                                />
                                <Route
                                    path="Form035/:referenceId"
                                    element={
                                        <Auth
                                            roles={[
                                                "C",
                                                "CH",
                                                "FR",
                                                "FO",
                                                "SA",
                                                "CLA",
                                            ]}
                                        >
                                            <Form035Approval />
                                        </Auth>
                                    }
                                />
                                <Route
                                    path="Form040/:referenceId"
                                    element={
                                        <Auth
                                            roles={[
                                                "C",
                                                "CH",
                                                "FR",
                                                "FO",
                                                "SA",
                                                "E",
                                                "CLA",
                                            ]}
                                        >
                                            <Form040Approval />
                                        </Auth>
                                    }
                                />
                                <Route
                                    path="Form041/:referenceId"
                                    element={
                                        <Auth roles={rolesExceptE}>
                                            <Form041Approval />
                                        </Auth>
                                    }
                                />
                                <Route
                                    path="Form042/:referenceId"
                                    element={
                                        <Auth roles={loggedInRoles}>
                                            <Form042Approval />
                                        </Auth>
                                    }
                                />
                                <Route
                                    path="Form043/:referenceId"
                                    element={
                                        <Auth
                                            roles={[
                                                "C",
                                                "CH",
                                                "FR",
                                                "FO",
                                                "SA",
                                                "E",
                                                "CLA",
                                            ]}
                                        >
                                            <Form043Approval />
                                        </Auth>
                                    }
                                />
                                <Route
                                    path="Form044/:referenceId"
                                    element={
                                        <Auth
                                            roles={[
                                                "C",
                                                "CH",
                                                "FR",
                                                "FO",
                                                "SA",
                                                "E",
                                                "CLA",
                                            ]}
                                        >
                                            <Form044Approval />
                                        </Auth>
                                    }
                                />
                                <Route
                                    path="Form045/:referenceId"
                                    element={
                                        <Auth
                                            roles={[
                                                "C",
                                                "CH",
                                                "FR",
                                                "FO",
                                                "SA",
                                                "E",
                                                "CLA",
                                            ]}
                                        >
                                            <Form045Approval />
                                        </Auth>
                                    }
                                />
                                <Route
                                    path="Form046/:referenceId"
                                    element={
                                        <Auth
                                            roles={[
                                                "C",
                                                "CH",
                                                "FR",
                                                "FO",
                                                "SA",
                                                "E",
                                                "CLA",
                                            ]}
                                        >
                                            <Form046Approval />
                                        </Auth>
                                    }
                                />
                                <Route
                                    path="Form047/:referenceId"
                                    element={
                                        <Auth roles={rolesExceptE}>
                                            <Form047Approval />
                                        </Auth>
                                    }
                                />
                                <Route
                                    path="Form048/:referenceId"
                                    element={
                                        <Auth
                                            roles={[
                                                "C",
                                                "CH",
                                                "FR",
                                                "FO",
                                                "SA",
                                                "E",
                                                "CLA",
                                            ]}
                                        >
                                            <Form048Approval />
                                        </Auth>
                                    }
                                />
                                <Route
                                    path="Form049/:referenceId"
                                    element={
                                        <Auth
                                            roles={[
                                                "C",
                                                "CH",
                                                "FR",
                                                "FO",
                                                "SA",
                                                "E",
                                                "CLA",
                                            ]}
                                        >
                                            <Form049Approval />
                                        </Auth>
                                    }
                                />
                                <Route
                                    path="Form051/:referenceId"
                                    element={
                                        <Auth roles={rolesExceptE}>
                                            <Form051Approval />
                                        </Auth>
                                    }
                                />
                                <Route
                                    path="Form053/:referenceId"
                                    element={
                                        <Auth
                                            roles={[
                                                "C",
                                                "CH",
                                                "FR",
                                                "FO",
                                                "SA",
                                                "E",
                                                "CLA",
                                            ]}
                                        >
                                            <Form053Approval />
                                        </Auth>
                                    }
                                />
                                <Route
                                    path="Form054/:referenceId"
                                    element={
                                        <Auth
                                            roles={[
                                                "C",
                                                "CH",
                                                "FR",
                                                "FO",
                                                "SA",
                                                "E",
                                                "CLA",
                                            ]}
                                        >
                                            <Form054Approval />
                                        </Auth>
                                    }
                                />
                                <Route
                                    path="Form050/:referenceId"
                                    element={
                                        <Auth
                                            roles={[
                                                "C",
                                                "CH",
                                                "CLA",
                                                "FR",
                                                "FO",
                                                "SA",
                                                "E",
                                                "Public",
                                            ]}
                                        >
                                            <Form050Approval />
                                        </Auth>
                                    }
                                />
                                <Route
                                    path="Form052/:referenceId"
                                    element={
                                        <Auth
                                            roles={[
                                                "C",
                                                "CH",
                                                "CLA",
                                                "FR",
                                                "FO",
                                                "SA",
                                                "E",
                                                "CLA",
                                            ]}
                                        >
                                            <Form052Approval />
                                        </Auth>
                                    }
                                />
                                <Route
                                    path="Form057/:referenceId"
                                    element={
                                        <Auth
                                            roles={[
                                                "C",
                                                "CH",
                                                "FR",
                                                "FO",
                                                "SA",
                                                "E",
                                                "CLA",
                                            ]}
                                        >
                                            <Form057Approval />
                                        </Auth>
                                    }
                                />
                                <Route
                                    path="Form058/:referenceId"
                                    element={
                                        <Auth
                                            roles={[
                                                "C",
                                                "CH",
                                                "FR",
                                                "FO",
                                                "SA",
                                                "E",
                                                "CLA",
                                            ]}
                                        >
                                            <Form058Approval />
                                        </Auth>
                                    }
                                />
                                <Route
                                    path="Form059/:referenceId"
                                    element={
                                        <Auth
                                            roles={[
                                                "C",
                                                "CH",
                                                "FR",
                                                "FO",
                                                "SA",
                                                "CLA",
                                            ]}
                                        >
                                            <Form059Approval />
                                        </Auth>
                                    }
                                />
                                <Route
                                    path="Form062/:referenceId"
                                    element={
                                        <Auth
                                            roles={[
                                                "C",
                                                "CH",
                                                "FR",
                                                "FO",
                                                "SA",
                                                "E",
                                                "CLA",
                                            ]}
                                        >
                                            <Form062Approval />
                                        </Auth>
                                    }
                                />
                                <Route
                                    path="Form063/:referenceId"
                                    element={
                                        <Auth
                                            roles={[
                                                "C",
                                                "CH",
                                                "FR",
                                                "FO",
                                                "SA",
                                                "E",
                                                "CLA",
                                            ]}
                                        >
                                            <Form063Approval />
                                        </Auth>
                                    }
                                />
                                <Route
                                    path="Form064/:referenceId"
                                    element={
                                        <Auth
                                            roles={[
                                                "C",
                                                "CH",
                                                "FR",
                                                "FO",
                                                "SA",
                                                "E",
                                                "CLA",
                                            ]}
                                        >
                                            <Form064Approval />
                                        </Auth>
                                    }
                                />
                                <Route
                                    path="Form065/:referenceId"
                                    element={
                                        <Auth
                                            roles={[
                                                "C",
                                                "CH",
                                                "FR",
                                                "FO",
                                                "SA",
                                                "E",
                                                "CLA",
                                            ]}
                                        >
                                            <Form065Approval />
                                        </Auth>
                                    }
                                />
                                <Route
                                    path="Form066/:referenceId"
                                    element={
                                        <Auth
                                            roles={[
                                                "C",
                                                "CH",
                                                "FR",
                                                "FO",
                                                "SA",
                                                "E",
                                                "CLA",
                                            ]}
                                        >
                                            <Form066Approval />
                                        </Auth>
                                    }
                                />
                                <Route
                                    path="Form067/:referenceId"
                                    element={
                                        <Auth
                                            roles={[
                                                "C",
                                                "CH",
                                                "FR",
                                                "FO",
                                                "SA",
                                                "E",
                                                "CLA",
                                            ]}
                                        >
                                            <Form067Approval />
                                        </Auth>
                                    }
                                />
                                <Route
                                    path="Form068/:referenceId"
                                    element={
                                        <Auth
                                            roles={[
                                                "C",
                                                "CH",
                                                "FR",
                                                "FO",
                                                "SA",
                                                "E",
                                                "CLA",
                                            ]}
                                        >
                                            <Form068Approval />
                                        </Auth>
                                    }
                                />
                                <Route
                                    path="Form069/:referenceId"
                                    element={
                                        <Auth
                                            roles={[
                                                "C",
                                                "CH",
                                                "FR",
                                                "FO",
                                                "SA",
                                                "E",
                                                "CLA",
                                            ]}
                                        >
                                            <Form069Approval />
                                        </Auth>
                                    }
                                />
                                <Route
                                    path="Form070/:referenceId"
                                    element={
                                        <Auth
                                            roles={[
                                                "C",
                                                "CH",
                                                "FR",
                                                "FO",
                                                "SA",
                                                "E",
                                                "CLA",
                                            ]}
                                        >
                                            <Form070Approval />
                                        </Auth>
                                    }
                                />
                                <Route
                                    path="Form071/:referenceId"
                                    element={
                                        <Auth
                                            roles={[
                                                "C",
                                                "CH",
                                                "FR",
                                                "FO",
                                                "SA",
                                                "E",
                                                "CLA",
                                            ]}
                                        >
                                            <Form071Approval />
                                        </Auth>
                                    }
                                />
                                <Route
                                    path="Form072/:referenceId"
                                    element={
                                        <Auth
                                            roles={[
                                                "C",
                                                "CH",
                                                "FR",
                                                "FO",
                                                "SA",
                                                "E",
                                                "CLA",
                                            ]}
                                        >
                                            <Form072Approval />
                                        </Auth>
                                    }
                                />
                                <Route
                                    path="Form073/:referenceId"
                                    element={
                                        <Auth
                                            roles={[
                                                "C",
                                                "CH",
                                                "CLA",
                                                "FR",
                                                "FO",
                                                "SA",
                                                "E",
                                                "CLA",
                                            ]}
                                        >
                                            <Form073Approval />
                                        </Auth>
                                    }
                                />
                                <Route
                                    path="Form074/:referenceId"
                                    element={
                                        <Auth
                                            roles={[
                                                "C",
                                                "CH",
                                                "CLA",
                                                "FR",
                                                "FO",
                                                "SA",
                                            ]}
                                        >
                                            <Form074Approval />
                                        </Auth>
                                    }
                                />
                                <Route
                                    path="Form075/:referenceId"
                                    element={
                                        <Auth
                                            roles={[
                                                "C",
                                                "CH",
                                                "FR",
                                                "FO",
                                                "SA",
                                                "CLA",
                                            ]}
                                        >
                                            <Form075Approval />
                                        </Auth>
                                    }
                                />
                                <Route
                                    path="Form076/:referenceId"
                                    element={
                                        <Auth roles={rolesExceptE}>
                                            <Form076Approval />
                                        </Auth>
                                    }
                                />
                                <Route
                                    path="Form078/:referenceId"
                                    element={
                                        <Auth
                                            roles={[
                                                "C",
                                                "CH",
                                                "CLA",
                                                "FR",
                                                "FO",
                                                "SA",
                                                "E",
                                            ]}
                                        >
                                            <Form078Approval />
                                        </Auth>
                                    }
                                />
                                <Route
                                    path="Form079/:referenceId"
                                    element={
                                        <Auth
                                            roles={[
                                                ...rolesExceptSANEdb,
                                                "FR",
                                                "FO",
                                                "SA",
                                            ]}
                                        >
                                            <Form079Approval />
                                        </Auth>
                                    }
                                />
                                <Route
                                    path="Form080/:referenceId"
                                    element={
                                        <Auth roles={rolesExceptE}>
                                            <Form080Approval />
                                        </Auth>
                                    }
                                />
                                <Route
                                    path="Form081/:referenceId"
                                    element={
                                        <Auth
                                            roles={[
                                                "C",
                                                "CH",
                                                "CLA",
                                                "FR",
                                                "FO",
                                                "SA",
                                                "E",
                                            ]}
                                        >
                                            <Form081Approval />
                                        </Auth>
                                    }
                                />
                                <Route
                                    path="Form082/:referenceId"
                                    element={
                                        <Auth
                                            roles={[
                                                "C",
                                                "CH",
                                                "CLA",
                                                "FR",
                                                "FO",
                                                "SA",
                                                "E",
                                            ]}
                                        >
                                            <Form082Approval />
                                        </Auth>
                                    }
                                />
                                <Route
                                    path="Form083/:referenceId"
                                    element={
                                        <Auth
                                            roles={[
                                                "C",
                                                "CH",
                                                "CLA",
                                                "FR",
                                                "FO",
                                                "SA",
                                                "E",
                                            ]}
                                        >
                                            <Form083Approval />
                                        </Auth>
                                    }
                                />
                                <Route
                                    path="Form084/:referenceId"
                                    element={
                                        <Auth
                                            roles={[
                                                "C",
                                                "CH",
                                                "CLA",
                                                "FR",
                                                "FO",
                                                "SA",
                                                "E",
                                            ]}
                                        >
                                            <Form084Approval />
                                        </Auth>
                                    }
                                />
                                <Route
                                    path="Form085/:referenceId"
                                    element={
                                        <Auth
                                            roles={[
                                                "C",
                                                "CH",
                                                "FR",
                                                "FO",
                                                "SA",
                                                "CLA",
                                            ]}
                                        >
                                            <Form085Approval />
                                        </Auth>
                                    }
                                />
                                <Route
                                    path="Form086/:referenceId"
                                    element={
                                        <Auth
                                            roles={[
                                                "C",
                                                "CH",
                                                "FR",
                                                "FO",
                                                "SA",
                                                "CLA",
                                            ]}
                                        >
                                            <Form086Approval />
                                        </Auth>
                                    }
                                />
                                <Route
                                    path="Form087/:referenceId"
                                    element={
                                        <Auth roles={rolesExceptE}>
                                            <Form087Approval />
                                        </Auth>
                                    }
                                />
                                <Route
                                    path="Form088/:referenceId"
                                    element={
                                        <Auth
                                            roles={[
                                                "C",
                                                "CH",
                                                "FR",
                                                "FO",
                                                "SA",
                                                "CLA",
                                            ]}
                                        >
                                            <Form088Approval />
                                        </Auth>
                                    }
                                />
                                <Route
                                    path="Form089/:referenceId"
                                    element={
                                        <Auth
                                            roles={[
                                                "C",
                                                "CH",
                                                "FR",
                                                "FO",
                                                "SA",
                                                "CLA",
                                            ]}
                                        >
                                            <Form089Approval />
                                        </Auth>
                                    }
                                />
                                <Route
                                    path="Form090/:referenceId"
                                    element={
                                        <Auth
                                            roles={[
                                                "C",
                                                "CH",
                                                "FR",
                                                "FO",
                                                "SA",
                                                "CLA",
                                            ]}
                                        >
                                            <Form090Approval />
                                        </Auth>
                                    }
                                />
                                <Route
                                    path="Form091/:referenceId"
                                    element={
                                        <Auth roles={rolesExceptE}>
                                            <Form091Approval />
                                        </Auth>
                                    }
                                />
                                <Route
                                    path="Form092/:referenceId"
                                    element={
                                        <Auth roles={loggedInRoles}>
                                            <Form092Approval />
                                        </Auth>
                                    }
                                />
                                <Route
                                    path="Form093/:referenceId"
                                    element={
                                        <Auth
                                            roles={[
                                                "C",
                                                "CH",
                                                "FR",
                                                "FO",
                                                "SA",
                                                "CLA",
                                            ]}
                                        >
                                            <Form093Approval />
                                        </Auth>
                                    }
                                />
                                <Route
                                    path="Form094/:referenceId"
                                    element={
                                        <Auth
                                            roles={[
                                                "C",
                                                "CH",
                                                "FR",
                                                "FO",
                                                "SA",
                                                "CLA",
                                            ]}
                                        >
                                            <Form094Approval />
                                        </Auth>
                                    }
                                />
                                <Route
                                    path="Form095/:referenceId"
                                    element={
                                        <Auth roles={loggedInRoles}>
                                            <Form095Approval />
                                        </Auth>
                                    }
                                />
                                <Route
                                    path="Form096/:referenceId"
                                    element={
                                        <Auth roles={rolesExceptE}>
                                            <Form096Approval />
                                        </Auth>
                                    }
                                />
                                <Route
                                    path="Form097/:referenceId"
                                    element={
                                        <Auth
                                            roles={[
                                                "C",
                                                "CH",
                                                "FR",
                                                "FO",
                                                "SA",
                                                "CLA",
                                            ]}
                                        >
                                            <Form097Approval />
                                        </Auth>
                                    }
                                />
                                <Route
                                    path="Form098/:referenceId"
                                    element={
                                        <Auth
                                            roles={[
                                                "C",
                                                "CH",
                                                "FR",
                                                "FO",
                                                "SA",
                                                "CLA",
                                            ]}
                                        >
                                            <Form098Approval />
                                        </Auth>
                                    }
                                />
                                <Route
                                    path="Form099/:referenceId"
                                    element={
                                        <Auth
                                            roles={[
                                                "C",
                                                "CH",
                                                "FR",
                                                "FO",
                                                "SA",
                                                "CLA",
                                            ]}
                                        >
                                            <Form099Approval />
                                        </Auth>
                                    }
                                />
                                <Route
                                    path="Form100/:referenceId"
                                    element={
                                        <Auth
                                            roles={[
                                                "C",
                                                "CH",
                                                "FR",
                                                "FO",
                                                "SA",
                                                "CLA",
                                                "Public",
                                            ]}
                                        >
                                            <Form100Approval />
                                        </Auth>
                                    }
                                />
                                <Route
                                    path="Form101/:referenceId"
                                    element={
                                        <Auth roles={rolesExceptE}>
                                            <Form101Approval />
                                        </Auth>
                                    }
                                />
                                <Route
                                    path="Form102/:referenceId"
                                    element={
                                        <Auth
                                            roles={[
                                                "S",
                                                "SH",
                                                "SLA",
                                                "FR",
                                                "FO",
                                                "SA",
                                                "CLA",
                                            ]}
                                        >
                                            <Form102Approval />
                                        </Auth>
                                    }
                                />
                                <Route
                                    path="Form103/:referenceId"
                                    element={
                                        <Auth roles={loggedInRoles}>
                                            <Form103Approval />
                                        </Auth>
                                    }
                                />
                                <Route
                                    path="Form104/:referenceId"
                                    element={
                                        <Auth roles={loggedInRoles}>
                                            <Form104Approval />
                                        </Auth>
                                    }
                                />
                                <Route
                                    path="Form105/:referenceId"
                                    element={
                                        <Auth roles={loggedInRoles}>
                                            <Form105Approval />
                                        </Auth>
                                    }
                                />
                                <Route
                                    path="Form106/:referenceId"
                                    element={
                                        <Auth roles={loggedInRoles}>
                                            <Form106Approval />
                                        </Auth>
                                    }
                                />
                                <Route
                                    path="Form107/:referenceId"
                                    element={
                                        <Auth roles={loggedInRoles}>
                                            <Form107Approval />
                                        </Auth>
                                    }
                                />
                            </Route>
                            <Route path="NotFound" element={<NotFound />} />
                        </Routes>
                    </AuthProvider>
                </Suspense>
            </HistoryRouter>
        </ThemeProvider>
    );
}

export default App;
